import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/common";
import { CheckCircle2, XCircle, AlertTriangle, Cpu } from "lucide-react";

export default function PipelineHealth() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/pipeline/health").then((r) => setD(r.data));
  }, []);
  if (!d) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading…</div>;

  return (
    <div className="p-8 max-w-[1400px]">
      <h1 className="font-head text-2xl font-bold tracking-tight mb-1">Pipeline Health</h1>
      <p className="text-xs font-mono text-[#71717A] mb-4">
        Data origin: <span className="text-emerald-400">{d.data_origin}</span> · Apify token configured:{" "}
        <span className={d.apify_token_configured ? "text-emerald-400" : "text-rose-400"}>{String(d.apify_token_configured)}</span> ·
        any source live: <span className={d.any_source_live ? "text-emerald-400" : "text-rose-400"}>{String(d.any_source_live)}</span>
      </p>

      <div data-testid="source-flags" className="flex flex-wrap gap-2 mb-6">
        {Object.entries(d.source_flags || {}).map(([src, on]) => (
          <span
            key={src}
            data-testid={`flag-${src}`}
            className={`text-[11px] font-mono border px-2 py-1 ${on ? "text-emerald-400 border-emerald-700 bg-emerald-950/30" : "text-[#71717A] border-[#27272A]"}`}
          >
            {src.toUpperCase()}_LIVE = {String(on)}
          </span>
        ))}
      </div>

      {d.trends_collector && (
        <div data-testid="trends-collector" className="border border-[#27272A] bg-[#18181B] p-4 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Google Trends Collector (US)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-xs">
            <div><span className="text-[#71717A]">actor</span><div className="text-[#F4F4F5] break-all">{d.trends_collector.actor.actor_id}</div></div>
            <div><span className="text-[#71717A]">pinned build</span><div className="text-emerald-400">{d.trends_collector.actor.actor_build}</div></div>
            <div><span className="text-[#71717A]">spent today / daily cap</span><div className="text-[#F4F4F5]">${d.trends_collector.budget.spent_today} / ${d.trends_collector.budget.daily_budget}</div></div>
            <div><span className="text-[#71717A]">spent month / monthly cap</span><div className="text-[#F4F4F5]">${d.trends_collector.budget.spent_month} / ${d.trends_collector.budget.monthly_budget}</div></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {d.source_status.map((s) => (
          <div key={s.source} data-testid={`source-status-${s.source}`} className="border border-[#27272A] bg-[#18181B] p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm uppercase tracking-widest">{s.source}</span>
              {s.last_success ? (
                <CheckCircle2 size={16} className="text-emerald-400" />
              ) : (
                <XCircle size={16} className="text-rose-400" />
              )}
            </div>
            <div className="text-[11px] font-mono text-[#A1A1AA] mt-2">
              last success: {s.last_success ? s.last_success.slice(0, 16).replace("T", " ") : "never"}
            </div>
            <div className="text-[10px] font-mono mt-1">
              live: <span className={s.live_enabled ? "text-emerald-400" : "text-[#71717A]"}>{String(s.live_enabled)}</span>
            </div>
            {s.stale && <div className="text-[10px] font-mono text-amber-400 mt-1">⚠ STALE (&gt;7d)</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="border border-[#27272A] bg-[#18181B] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono">Total runs</div>
          <div className="font-mono text-2xl font-bold">{d.total_runs}</div>
        </div>
        <div className="border border-[#27272A] bg-[#18181B] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono">Est. cost (USD)</div>
          <div className="font-mono text-2xl font-bold">${d.total_cost_estimate}</div>
        </div>
        <div className="border border-[#27272A] bg-[#18181B] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono flex items-center gap-1"><Cpu size={12} /> Jobs</div>
          <div className="font-mono text-sm mt-1">
            <span className="text-emerald-400">{d.jobs.done} done</span> ·{" "}
            <span className="text-amber-400">{d.jobs.pending} pending</span> ·{" "}
            <span className="text-rose-400">{d.jobs.dead_letter} dead-letter</span>
          </div>
        </div>
      </div>

      <Panel title="Actor failures (fault isolated — pipeline continued)" right={<AlertTriangle size={14} className="text-rose-400" />}>
        {d.failures.length === 0 ? (
          <div className="text-xs font-mono text-[#71717A]">No failures.</div>
        ) : (
          <div className="space-y-2">
            {d.failures.map((f) => (
              <div key={f.id} data-testid={`failure-${f.source}`} className="border border-rose-900/50 bg-rose-950/10 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-rose-400 uppercase">{f.source}</span>
                  <span className="font-mono text-[10px] text-[#71717A]">{f.actor_id}</span>
                </div>
                <div className="text-[11px] font-mono text-[#A1A1AA] mt-1">{f.error}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
